import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNotifications } from "../../contexts/NotificationContext";

type Sprite = {
	x: number;
	y: number;
	width: number;
	height: number;
};

const SpaceShooter = () => {
	const { addNotification } = useNotifications();
	const [isPlaying, setIsPlaying] = useState(false);
	const [score, setScore] = useState(0);
	const [player, setPlayer] = useState<Sprite>({
		x: 50,
		y: 90,
		width: 40,
		height: 40,
	});
	const [bullets, setBullets] = useState<Sprite[]>([]);
	const [enemies, setEnemies] = useState<Sprite[]>([]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (!isPlaying) return;

			switch (e.key) {
				case "ArrowLeft":
					setPlayer((prev) => ({
						...prev,
						x: Math.max(0, prev.x - 5),
					}));
					break;
				case "ArrowRight":
					setPlayer((prev) => ({
						...prev,
						x: Math.min(90, prev.x + 5),
					}));
					break;
				case " ":
					setBullets((prev) => [
						...prev,
						{
							x: player.x + player.width / 2,
							y: player.y,
							width: 4,
							height: 10,
						},
					]);
					break;
			}
		},
		[isPlaying, player]
	);

	// Game loop
	useEffect(() => {
		if (!isPlaying) return;

		const gameLoop = setInterval(() => {
			// Move bullets
			setBullets((prev) =>
				prev
					.map((bullet) => ({
						...bullet,
						y: bullet.y - 2,
					}))
					.filter((bullet) => bullet.y > 0)
			);

			// Move enemies
			setEnemies((prev) =>
				prev
					.map((enemy) => ({
						...enemy,
						y: enemy.y + 1,
					}))
					.filter((enemy) => enemy.y < 100)
			);

			// Spawn enemies
			if (Math.random() < 0.05) {
				setEnemies((prev) => [
					...prev,
					{
						x: Math.random() * 90,
						y: -10,
						width: 30,
						height: 30,
					},
				]);
			}

			// Check collisions
			bullets.forEach((bullet) => {
				enemies.forEach((enemy) => {
					if (
						bullet.x < enemy.x + enemy.width &&
						bullet.x + bullet.width > enemy.x &&
						bullet.y < enemy.y + enemy.height &&
						bullet.y + bullet.height > enemy.y
					) {
						setScore((prev) => prev + 100);
						setEnemies((prev) => prev.filter((e) => e !== enemy));
						setBullets((prev) => prev.filter((b) => b !== bullet));
					}
				});
			});

			// Check game over
			enemies.forEach((enemy) => {
				if (enemy.y + enemy.height > player.y) {
					setIsPlaying(false);
					addNotification({
						message: `Game Over! Score: ${score}`,
						type: "info",
						icon: "🚀",
					});
				}
			});
		}, 1000 / 60);

		return () => clearInterval(gameLoop);
	}, [isPlaying, bullets, enemies, player, score, addNotification]);

	useEffect(() => {
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);

	return (
		<div className='relative h-96 bg-black/30 rounded-xl overflow-hidden'>
			{!isPlaying ? (
				<button
					onClick={() => setIsPlaying(true)}
					className='absolute inset-0 flex flex-col items-center justify-center text-trump-yellow font-bold'>
					<span className='text-4xl mb-2'>🚀</span>
					<span className='text-xl'>START SPACE SHOOTER</span>
					<span className='text-sm text-white/60 mt-2'>
						Use arrows to move, space to shoot!
					</span>
				</button>
			) : (
				<>
					<div className='absolute top-2 left-2 bg-black/50 px-4 py-2 rounded-full text-trump-yellow font-bold'>
						Score: {score}
					</div>
					{/* Player */}
					<motion.div
						className='absolute'
						style={{
							left: `${player.x}%`,
							top: `${player.y}%`,
							width: `${player.width}px`,
							height: `${player.height}px`,
						}}>
						<img
							src='/player-ship.png'
							alt='Player'
							className='w-full h-full object-contain'
						/>
					</motion.div>
					{/* Bullets */}
					{bullets.map((bullet, index) => (
						<motion.div
							key={index}
							className='absolute bg-trump-yellow rounded-full'
							style={{
								left: `${bullet.x}%`,
								top: `${bullet.y}%`,
								width: `${bullet.width}px`,
								height: `${bullet.height}px`,
							}}
						/>
					))}
					{/* Enemies */}
					{enemies.map((enemy, index) => (
						<motion.div
							key={index}
							className='absolute'
							style={{
								left: `${enemy.x}%`,
								top: `${enemy.y}%`,
								width: `${enemy.width}px`,
								height: `${enemy.height}px`,
							}}>
							<img
								src='/enemy-ship.png'
								alt='Enemy'
								className='w-full h-full object-contain'
							/>
						</motion.div>
					))}
				</>
			)}
		</div>
	);
};

export default SpaceShooter;
